import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import type { PaginationState } from '@tanstack/react-table'
import { getPaginationRowModel, getCoreRowModel } from '@tanstack/react-table'
import { RefreshCw } from 'lucide-react'
import dayjs from 'dayjs'
import { useAtomValue } from 'jotai'

import { DataTable } from '@/components/ui/data-table'
import { Pagination } from '@/components/ui/pagination'
import { Button } from '@/components/ui/button'
import { FeedbackSummaryBar } from '@/components/ui/feedback-bar'
import { PageHeader } from '@/components/ui/page-header'

import {
    fetchProblemTypes,
    fetchFeedbackSummary,
    fetchFeedbacks,
    fetchSatisfactionScores,
} from '@/api/feedbacks.api'
import { buildColumns } from './columnsCounter'
import { FeedbackFilterPanel } from './CounterFeedbackFilterPanel'
import type { FeedbackFilterValues } from './CounterFeedbackFilterPanel'
import type { Feedback } from '@/types/api'
import { currentUserAtom } from '@/store/auth.atom'
import { FeedbackDetailDrawer } from './FeedbackDetailDrawer'
import { formatEndDate, formatStartDate } from '@/utils/format'
import { exportSurvey, useTableExport } from '@/api/dowload-excel.api'
import { usePermission } from '@/hooks/use-permission'

const DEFAULT_FILTER: FeedbackFilterValues = {
    surveyType: undefined,
    satisfactionScore: undefined,
    robotId: undefined,
    robotName: undefined,
    dateRange: undefined,
    unitId: undefined,
    unitName: undefined,
    deskNumber: undefined,
}

function toApiParams(f: FeedbackFilterValues) {
    const scoreFromFilter = f.satisfactionScore
        ? Number(f.satisfactionScore)
        : undefined

    const fromDateRaw = f.dateRange?.from
    const toDateRaw = f.dateRange?.to

    return {
        problemType: f.surveyType || undefined,
        satisfactionScore:
            typeof scoreFromFilter === 'number' && !Number.isNaN(scoreFromFilter)
                ? [scoreFromFilter]
                : undefined,
        robotId: f.robotId || undefined,
        unitId: f.unitId || undefined,
        deskNumber: f.deskNumber || undefined,
        fromDate: formatStartDate(fromDateRaw || dayjs().startOf('month')),
        toDate: formatEndDate(toDateRaw || dayjs()),
    }
}

export function CounterFeedbackTab() {
    const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 10,
    })
    const currentUser = useAtomValue(currentUserAtom)
    const showUnitColumn = currentUser?.userType === 1
    const canView = usePermission('survey-view')
    const canExport = usePermission('survey-export')

    const [showFilter, setShowFilter] = useState(false)
    const [draftFilter, setDraftFilter] =
        useState<FeedbackFilterValues>(DEFAULT_FILTER)
    const [appliedFilter, setAppliedFilter] =
        useState<FeedbackFilterValues>(DEFAULT_FILTER)
    const resolvedUnitId = showUnitColumn
        ? appliedFilter.unitId
        : currentUser?.unitId
    const [selectedScores, setSelectedScores] = useState<number[]>([])
    const [viewingFeedback, setViewingFeedback] = useState<Feedback | null>(null)
    const [isExporting, setIsExporting] = useState(false)

    const apiParams = useMemo(() => {
        const base = toApiParams({
            ...appliedFilter,
            unitId: resolvedUnitId,
        })

        const scoreFromFilter = base.satisfactionScore

        return {
            ...base,
            satisfactionScore:
                scoreFromFilter ?? (selectedScores.length ? selectedScores : undefined),
        }
    }, [appliedFilter, resolvedUnitId, selectedScores])

    const { data: problemTypes = [] } = useQuery({
        queryKey: ['problem-types'],
        queryFn: ({ signal }) => fetchProblemTypes(signal),
        refetchOnMount: 'always',
        staleTime: 5 * 60 * 1000,
    })

    const { data: satisfactionScores = [] } = useQuery({
        queryKey: ['satisfaction-scores'],
        queryFn: ({ signal }) => fetchSatisfactionScores(signal),
        refetchOnMount: 'always',
        staleTime: Infinity,
    })

    const { data, isLoading, refetch } = useQuery({
        queryKey: ['feedbacks', pageIndex, pageSize, apiParams],
        queryFn: ({ signal }) =>
            fetchFeedbacks(pageIndex, pageSize, apiParams, signal),
        refetchOnMount: 'always',
        refetchInterval: 30 * 60 * 1000,
    })

    const hasData = Boolean(data?.items && data.items.length > 0)

    const surveyTypeMap = useMemo(
        () => new Map(problemTypes.map((t) => [t.id, t.name])),
        [problemTypes],
    )

    const summaryFilters = useMemo(() => {
        return toApiParams({
            ...appliedFilter,
            unitId: resolvedUnitId,
        })
    }, [appliedFilter, resolvedUnitId])

    const { data: summary, refetch: refetchSummary } = useQuery({
        queryKey: ['feedbacks', 'summary', summaryFilters],
        queryFn: ({ signal }) =>
            fetchFeedbackSummary(satisfactionScores, summaryFilters, signal),
        enabled: satisfactionScores.length > 0,
        refetchOnMount: 'always',
        refetchInterval: 30 * 60 * 1000,
    })

    const columns = useMemo(
        () =>
            buildColumns({
                pageIndex,
                pageSize,
                canView,
                surveyTypeMap,
                showUnitColumn,
                onViewDetail: setViewingFeedback,
            }),
        [pageIndex, pageSize, canView, surveyTypeMap, showUnitColumn],
    )

    const { handleExport } = useTableExport<Feedback>(
        'Danh sách đánh giá',
        columns,
        async (payload) => {
            try {
                setIsExporting(true)
                await exportSurvey(apiParams, payload, 'danh-sach-danh-gia.xlsx')
            } finally {
                setIsExporting(false)
            }
        },
    )

    return (
        <div className="space-y-4">
            <div className="px-6 !mb--7">
                <PageHeader
                    title="DANH SÁCH ĐÁNH GIÁ QUẦY"
                    onToggleFilter={() => setShowFilter((v) => !v)}
                    onExport={canExport ? handleExport : undefined}
                    disabled={!hasData}
                    isExporting={isExporting}
                >
                    <Button
                        variant="primary"
                        size="md"
                        disabled={isLoading}
                        onClick={() => {
                            setDraftFilter(DEFAULT_FILTER)
                            setAppliedFilter(DEFAULT_FILTER)
                            setSelectedScores([])
                            setPagination((s) => ({ ...s, pageIndex: 0 }))
                            refetch()
                            refetchSummary()
                        }}
                        className="flex-1 gap-2 sm:flex-none h-10"
                    >
                        <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
                        Làm mới
                    </Button>
                </PageHeader>
            </div>

            {showFilter && (
                <FeedbackFilterPanel
                    values={draftFilter}
                    onChange={setDraftFilter}
                    surveyTypes={problemTypes}
                    satisfactionScores={satisfactionScores}
                    onSearch={() => {
                        setAppliedFilter(draftFilter)
                        if (draftFilter.satisfactionScore) {
                            setSelectedScores([])
                        }
                        setPagination((s) => ({ ...s, pageIndex: 0 }))
                        refetch()
                    }}
                />
            )}

            <DataTable
                columns={columns}
                data={data?.items ?? []}
                isLoading={isLoading}
                headertable={
                    summary ? (
                        <FeedbackSummaryBar
                            data={summary}
                            selectedScores={selectedScores}
                            onToggleScore={(score) => {
                                setSelectedScores((prev) => {
                                    const exists = prev.includes(score)
                                    const next = exists
                                        ? prev.filter((s) => s !== score)
                                        : [...prev, score]
                                    return next
                                })
                                setPagination((s) => ({ ...s, pageIndex: 0 }))
                            }}
                        />
                    ) : null
                }
                tableOptions={{
                    state: {
                        pagination: { pageIndex, pageSize },
                    },
                    onPaginationChange: setPagination,
                    getPaginationRowModel: getPaginationRowModel(),
                    getCoreRowModel: getCoreRowModel(),
                    manualPagination: true,
                    pageCount: data?.totalPages,
                }}
                footer={
                    <Pagination
                        page={pageIndex + 1}
                        pageCount={data?.totalPages ?? 1}
                        pageSize={pageSize}
                        totalCount={data?.total ?? 0}
                        onPageChange={(p) =>
                            setPagination((s) => ({ ...s, pageIndex: p - 1 }))
                        }
                        onPageSizeChange={(size) =>
                            setPagination({ pageIndex: 0, pageSize: size })
                        }
                    />
                }
            />

            <FeedbackDetailDrawer
                feedback={viewingFeedback}
                onClose={() => setViewingFeedback(null)}
                showUnitColumn={showUnitColumn}
            />
        </div>
    )
}
